import { useEffect, useRef, useState } from 'react';

const products = [
  {
    name: '딸기용 투명 팩 (500g~2,000g)',
    category: '플라스틱 팩',
    unit: '방울토마토, 딸기, 포도',
    img: 'https://static.readdy.ai/image/8c1d21b01113fd26c1c387000dbd0877/e077f7bcafd1c10f9a137f265acdb743.jpeg',
    color: '#0A508C',
  },
  {
    name: '소형 용기 (125g~500g)',
    category: '플라스틱 팩',
    unit: '블루베리, 체리, 산딸기',
    img: 'https://static.readdy.ai/image/8c1d21b01113fd26c1c387000dbd0877/db685704ba1a05322f9c66eb6a567f1d.png',
    color: '#3A7CC0',
  },
  {
    name: '만감류',
    category: '플라스틱 팩',
    unit: '사과, 배, 품종 귤',
    img: 'https://static.readdy.ai/image/8c1d21b01113fd26c1c387000dbd0877/b29c75bbfbb95c47a11f135735449ad7.png',
    color: '#2BC0D0',
  },
  {
    name: '프리미엄 특수 용기',
    category: '플라스틱 팩',
    unit: '원통형, 핸디형 팩',
    img: 'https://static.readdy.ai/image/8c1d21b01113fd26c1c387000dbd0877/60d2e894dc4e859358d3125bad1530d2.jpeg',
    color: '#70C6A3',
  },
];

export default function SmartStoreSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="smartstore" ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          {/* Left */}
          <div className={`lg:col-span-3 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold text-white mb-6" style={{ background: '#2BC0D0' }}>
              <i className="ri-store-2-line text-sm" />
              바로 구매 가능
            </div>
            <h2 className="text-4xl font-black text-gray-900 leading-tight mb-4">
              인기 패키지<br />제품 미리보기
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed mb-8">
              스마트스토어에서 다양한 패키지를 바로 구매할 수 있습니다. 
              소량 주문부터 대량 구매까지 가능하며, 라벨 맞춤 제작도 함께 문의하세요.
            </p>

            <div className="flex flex-col gap-3">
              <a
                href="https://smartstore.naver.com"
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-white font-bold text-sm cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #0A508C, #2BC0D0)' }}
              >
                <i className="ri-store-2-line" />
                스마트스토어 바로가기
                <i className="ri-external-link-line text-sm" />
              </a>
              <a
                href="https://smartstore.naver.com"
                target="_blank"
                rel="nofollow noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap border-2 transition-all hover:bg-[#0A508C]/5"
                style={{ borderColor: '#0A508C', color: '#0A508C' }}
              >
                <i className="ri-shopping-cart-line" />
                제품 구매하기
              </a>
            </div>

            {/* Trust badges */}
            <div className="mt-8 flex flex-col gap-2">
              {[
                { icon: 'ri-shield-check-line', text: '네이버 스마트스토어 공식 입점', color: '#70C6A3' },
                { icon: 'ri-truck-line', text: '빠른 배송, 안전한 포장', color: '#2BC0D0' },
                { icon: 'ri-customer-service-2-line', text: '구매 전 전화·채팅 상담 가능', color: '#3A7CC0' },
              ].map((b) => (
                <div key={b.text} className="flex items-center gap-2 text-sm text-gray-600">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0" style={{ background: b.color + '22' }}>
                    <i className={`${b.icon} text-xs`} style={{ color: b.color }} />
                  </div>
                  {b.text}
                </div>
              ))}
            </div>
          </div>

          {/* Right - Product List */}
          <div className={`lg:col-span-2 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex flex-col gap-3">
              {products.map((product, i) => (
                <div
                  key={product.name}
                  className={`flex items-center gap-4 bg-white rounded-2xl border border-gray-100 overflow-hidden transition-all duration-700 hover:-translate-y-0.5 ${visible ? 'opacity-100' : 'opacity-0'}`}
                  style={{ transitionDelay: `${(i + 1) * 100}ms` }}
                >
                  {/* 사진 */}
                  <div className="w-32 h-32 flex-shrink-0">
                    <img src={product.img} alt={product.name} className="w-full h-full object-cover object-center" />
                  </div>
                  {/* 설명 */}
                  <div className="flex flex-col flex-1 py-5 pr-5 gap-2">
                    {/* 상단: 제품명 + 상품보기 */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-black text-gray-900 text-base leading-snug">{product.name}</div>
                      <a
                        href="https://smartstore.naver.com"
                        target="_blank"
                        rel="nofollow noreferrer"
                        className="inline-flex items-center gap-1 text-sm font-bold cursor-pointer whitespace-nowrap transition-opacity hover:opacity-70 flex-shrink-0 mt-0.5"
                        style={{ color: product.color }}
                      >
                        상품 보기
                        <i className="ri-arrow-right-line" />
                      </a>
                    </div>
                    {/* 하단: 카테고리 뱃지 + 과일 정보 */}
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold text-white px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: product.color }}>{product.category}</span>
                      <span className="text-xs text-gray-400">{product.unit}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* View More */}
            <a
              href="https://smartstore.naver.com"
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center justify-center gap-2 py-4 rounded-2xl border-2 border-dashed text-sm font-bold cursor-pointer whitespace-nowrap transition-colors hover:bg-gray-50 mt-4"
              style={{ borderColor: '#2BC0D0', color: '#2BC0D0' }}
            >
              <i className="ri-add-circle-line" />
              더 많은 제품 보러가기
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
